import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useApp } from '@/contexts/AppContext';
import { aiService } from '@/services/aiService';
import { CompleteNotesResponse } from '@/types/ai';

interface AICompletionDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (sections: CompleteNotesResponse) => void;
}

const SECTIONS = [
  {
    id: 'system_information',
    label: 'System Information',
    description: 'Application overview and architecture summary',
  },
  {
    id: 'management_summary',
    label: 'Management Summary',
    description: 'Vulnerability summary with criticality',
  },
  {
    id: 'auth_concept',
    label: 'Authentication & Authorization Concept',
    description: 'Auth system explanation from connections',
  },
];

export function AICompletionDialog({
  open,
  onClose,
  onComplete,
}: AICompletionDialogProps) {
  const { currentDiagram, threats, projectInfo } = useApp();
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [systemDescription, setSystemDescription] = useState<string>(
    projectInfo?.description || currentDiagram?.system_description || ''
  );
  const [loadingSections, setLoadingSections] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggleSection = (sectionId: string) => {
    setSelectedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleGenerate = async () => {
    if (selectedSections.length === 0 || !currentDiagram) return;

    setIsGenerating(true);
    setLoadingSections(selectedSections);
    setError(null);

    try {
      // If the user provided a custom system description in the dialog, use it
      const diagramData = {
        entities: currentDiagram.entities,
        paths: currentDiagram.paths,
        // Include threats for this diagram OR global threats (diagram_id === null)
        threats: threats.filter((t) => 
          t.diagram_id === currentDiagram.id || t.diagram_id === null
        ),
        system_description: systemDescription || currentDiagram?.system_description || '',
        project_info: projectInfo ? {
          name: projectInfo.name,
          description: projectInfo.description,
        } : undefined,
      };

      const result = await aiService.completeNotes(
        diagramData,
        selectedSections
      );

      onComplete(result);
      // Persist provided system description back into diagram
      if (systemDescription && systemDescription !== currentDiagram?.system_description) {
        // set via app state - partial update
        // we don't have setAppState here, so caller should ensure persistence; alternatively we can omit.
      }
      onClose();
      
      // Reset state
      setSelectedSections([]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'AI completion failed';
      setError(errorMessage);
      console.error('AI completion error:', err);
    } finally {
      setIsGenerating(false);
      setLoadingSections([]);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      setSelectedSections([]);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>AI-Assisted Notes Completion</DialogTitle>
          <DialogDescription>
            Select which sections to generate automatically using AI
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.id} className="flex items-start gap-3">
              <Checkbox
                id={section.id}
                checked={selectedSections.includes(section.id)}
                onCheckedChange={() => handleToggleSection(section.id)}
                disabled={isGenerating}
              />
              <div className="flex-1">
                <label
                  htmlFor={section.id}
                  className="font-medium cursor-pointer"
                >
                  {section.label}
                </label>
                <p className="text-sm text-muted-foreground">
                  {section.description}
                </p>
              </div>
              {loadingSections.includes(section.id) && (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              )}
            </div>
          ))}

          {/* System description input shown when System Information is selected */}
          {selectedSections.includes('system_information') && (
            <div className="mt-2">
              <label className="block text-sm font-medium">System functional description (optional)</label>
              <Textarea
                value={systemDescription}
                onChange={(e) => setSystemDescription(e.target.value)}
                className="mt-1 w-full"
                placeholder="Briefly describe the system functionality to help the AI generate better system information..."
              />
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={selectedSections.length === 0 || isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
